import { orderBy } from "lodash";
import { ANY_PROFESSION_ID } from "constants/skuQualificationConstants";
import { SkuQualification } from "services/KnowfullyAdminClient";
import {
  getAggregatedValue,
  toKeyPart,
} from "helpers/skuQualificationHelpers";

// Pre-grouped-view shape: rows collapse by Accreditation Body + Profession + dates +
// credits, and the members of a collapsed row differ only by State.
export type LegacyQualificationGroup =
  | (Pick<
      SkuQualification,
      | "id"
      | "skuId"
      | "qualification"
      | "profession"
      | "stateId"
      | "stateName"
      | "releaseDate"
      | "creditSubmissionExpirationDate"
      | "verificationStatus"
      | "verificationMessage"
      | "immutable"
      | "courseNumber"
      | "approvalCode"
      | "approvalDate"
      | "credits"
      | "minimumCredit"
      | "partialCreditIncrement"
    > & {
      isGroup: true;
      childQualifications?: SkuQualification[];
      stateCount?: number;
      notes?: string;
    })
  | (SkuQualification & { isGroup: false });

const identityKey = (sq: SkuQualification): string =>
  [
    sq.qualification!.id,
    sq.profession?.id || ANY_PROFESSION_ID,
    toKeyPart(sq.releaseDate),
    toKeyPart(sq.creditSubmissionExpirationDate),
    toKeyPart(sq.courseNumber),
    toKeyPart(sq.approvalCode),
    toKeyPart(sq.approvalDate),
    toKeyPart(sq.credits),
    toKeyPart(sq.minimumCredit),
    toKeyPart(sq.partialCreditIncrement),
  ].join("_");

export const groupQualificationsByState = (
  qualifications: SkuQualification[],
): LegacyQualificationGroup[] => {
  const groups = new Map<string, SkuQualification[]>();

  for (const sq of qualifications) {
    const key = identityKey(sq);
    const group = groups.get(key);
    if (group) {
      group.push(sq);
    } else {
      groups.set(key, [sq]);
    }
  }

  const result: LegacyQualificationGroup[] = [];
  groups.forEach((children, key) => {
    if (children.length === 1) {
      result.push({ ...children[0], isGroup: false } as LegacyQualificationGroup);
      return;
    }

    const [firstChild] = children;
    const sortedChildren = [...children].sort((a, b) =>
      (a.stateName || "").localeCompare(b.stateName || ""),
    );

    result.push({
      ...firstChild,
      id: `group_${key}`,
      isGroup: true,
      childQualifications: sortedChildren,
      stateCount: sortedChildren.length,
      stateName: undefined,
      stateId: undefined,
      notes: getAggregatedValue(sortedChildren, "notes"),
    } as LegacyQualificationGroup);
  });

  return orderBy(
    result,
    ["releaseDate", "creditSubmissionExpirationDate"],
    ["asc", "asc"],
  );
};
